// DeclineCancellationModal — staff reject a customer's cancellation
// request and (optionally) notify the customer of the decision.
//
// Server contract (server/src/modules/scrap/bookings/bookings.types.ts
// declineCancellationSchema):
//   POST /api/v1/collections/bookings/:enc_id/decline-cancellation
//     body: { reason, notify_email, notify_sms, decline_reason? }
//   - `reason` is the staff-only audit-log entry (required, ≥1 char).
//   - `decline_reason` is the customer-facing explanation that lands in
//     the `cancellation_declined` template's {{decline_reason}} variable.
//   - When neither notify_email nor notify_sms is set, no notification
//     is enqueued (server skips silently).

import { useEffect, useState } from 'react';
import axios from 'axios';
import { Modal } from '../../../components/ui/Modal/Modal';
import { Button } from '../../../components/ui/Button/Button';
import { Textarea } from '../../../components/form/Textarea';
import { Checkbox } from '../../../components/form/Checkbox';
import { apiClient } from '../../../api/client';
import { endpoints } from '../../../api/endpoints';
import { useToast } from '../../../hooks/useToast';
import type { DeclineCancellationPayload } from '../../../types/api';
import styles from './DeclineCancellationModal.module.css';

const DECLINE_REASON_MAX = 500;
const STAFF_REASON_DEFAULT = 'Cancellation rejected by staff';

export interface DeclineCancellationModalProps {
  open: boolean;
  bookingEncId: string | null;
  bookingReference: string | null;
  customerName: string | null;
  onClose: () => void;
  onSuccess: (encId: string) => void;
}

export function DeclineCancellationModal({
  open,
  bookingEncId,
  bookingReference,
  customerName,
  onClose,
  onSuccess,
}: DeclineCancellationModalProps): JSX.Element | null {
  const toast = useToast();
  const [declineReason, setDeclineReason] = useState('');
  const [notifyEmail, setNotifyEmail] = useState(true);
  const [notifySms, setNotifySms] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setDeclineReason('');
      setNotifyEmail(true);
      setNotifySms(false);
      setSubmitting(false);
      setErrorMsg(null);
    }
  }, [open, bookingEncId]);

  const reasonOver = declineReason.length > DECLINE_REASON_MAX;
  const canSubmit = !reasonOver && !submitting && bookingEncId !== null;

  async function handleSubmit() {
    if (!canSubmit || bookingEncId === null) return;
    setSubmitting(true);
    setErrorMsg(null);
    const payload: DeclineCancellationPayload = {
      reason: STAFF_REASON_DEFAULT,
      notify_email: notifyEmail,
      notify_sms: notifySms,
      ...(declineReason.trim().length > 0
        ? { decline_reason: declineReason.trim() }
        : {}),
    };
    try {
      await apiClient.post(
        endpoints.bookings.declineCancellation(bookingEncId),
        payload,
      );
      toast.success('Cancellation request declined');
      onSuccess(bookingEncId);
      onClose();
    } catch (err) {
      const message = axios.isAxiosError(err)
        ? (err.response?.data as { message?: string } | undefined)?.message ??
          'Could not decline cancellation request.'
        : 'Could not decline cancellation request.';
      setErrorMsg(message);
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  }

  if (!open) return null;

  return (
    <Modal
      open={open}
      onClose={submitting ? () => undefined : onClose}
      title="Decline Cancellation Request"
      size="md"
      preventClose={submitting}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleSubmit}
            loading={submitting}
            disabled={!canSubmit}
          >
            Decline Request
          </Button>
        </>
      }
    >
      <div className={styles.body}>
        <p className={styles.subtitle}>
          You are declining the cancellation request for booking{' '}
          <strong>{bookingReference ?? '—'}</strong>
          {customerName ? (
            <>
              {' '}
              from <strong>{customerName}</strong>
            </>
          ) : null}
          .
        </p>

        <div className={styles.field}>
          <label className={styles.label} htmlFor="decline-reason">
            Decline reason
          </label>
          <Textarea
            id="decline-reason"
            value={declineReason}
            onChange={(e) => setDeclineReason(e.target.value)}
            rows={3}
            autoResize
            placeholder="Reason for declining (optional — shown to customer if notified)"
            aria-invalid={reasonOver || undefined}
            aria-label="Decline reason"
          />
          <span
            className={reasonOver ? styles.counterError : styles.counter}
          >
            {declineReason.length} / {DECLINE_REASON_MAX}
          </span>
        </div>

        <div className={styles.field}>
          <span className={styles.label}>Notify customer of decision</span>
          <div className={styles.toggleRow}>
            <Checkbox
              checked={notifyEmail}
              onChange={(e) => setNotifyEmail(e.target.checked)}
              label="Email"
            />
            <Checkbox
              checked={notifySms}
              onChange={(e) => setNotifySms(e.target.checked)}
              label="SMS"
            />
          </div>
        </div>

        {errorMsg !== null ? (
          <p role="alert" className={styles.error}>
            {errorMsg}
          </p>
        ) : null}
      </div>
    </Modal>
  );
}
